import { useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useRef, useState } from 'react';

import Honeybadger from '@honeybadger-io/js';

import { submitWizardOrchestrator } from 'api/Wizard/Wizard';
import { completeVideoProgress, updateVideoProgress } from 'api/Video';

import { useCustomMutation } from 'lib/hooks/useCustomMutation';

import { showAlert } from 'components/shared/Alert/Alert';

const PROGRESS_EVENTS = ['heartbeat', 'pause', 'seek'];
const MIN_POSITION_DELTA_SECONDS = 1;

const notifyCompletionFailed = () =>
  showAlert({
    color: 'danger',
    title: "We couldn't record your training completion — please replay the ending to try again."
  });

/**
 * Playback side-effects for a wizard training video: throttled progress pings,
 * completion recording, silent substep sync, and resume-from-last-position.
 *
 * @returns {{ playerRef: Object, hasEnded: boolean, trackProgress: Function }}
 *   playerRef attaches to VideoPlayer; trackProgress is its onAnalytics callback
 */
export default function useTrainingVideoPlayback({
  isAdmin,
  video,
  orchestratorData,
  savedProgress,
  progressFetching
}) {
  const navigate = useNavigate();
  const playerRef = useRef(null);
  const lastSentPositionRef = useRef(null);
  const hasResumedRef = useRef(false);
  const [hasEnded, setHasEnded] = useState(false);
  const [playerReady, setPlayerReady] = useState(false);

  // Plain fire-and-forget (not a react-query mutation): progress pings fire every few
  // seconds during playback and a mutation's state changes would re-render the player —
  // react-player v3 re-applies volume/playbackRate on every re-render, disrupting playback
  const sendProgressUpdate = useCallback(
    (params) => {
      updateVideoProgress(navigate, params).catch(() => {});
    },
    [navigate]
  );

  const { mutate: sendProgressComplete } = useCustomMutation({
    mutationFn: (params) => completeVideoProgress(navigate, params),
    invalidateQueryKey: ['video-progress', 'wizard-overview'],
    disableAlert: true,
    handleResponse: notifyCompletionFailed
  });

  // Silent submission (no navigation): flips the substep to Completed as soon as the
  // last assigned watcher finishes, instead of waiting for someone to click Continue
  const { mutate: syncSubstepStatus } = useCustomMutation({
    mutationFn: (params) => submitWizardOrchestrator(navigate, params),
    invalidateQueryKey: [['wizardInstance'], ['wizard-overview']],
    disableAlert: true
  });

  const trackProgress = useCallback(
    ({ type, currentTime, duration }) => {
      if (type === 'ready') {
        setPlayerReady(true);
        return;
      }

      if (isAdmin || !video?.id) return;

      if (type === 'ended') {
        setHasEnded(true);
        const finalPosition = Number(currentTime) || Number(duration) || 0;
        if (finalPosition > 0) {
          sendProgressUpdate({ video_id: video.id, current_position_seconds: finalPosition });
        }
        sendProgressComplete(
          { video_id: video.id },
          {
            // Sync the substep only when the completion actually recorded (code 0) —
            // requestApi resolves business errors too, so react-query's onSuccess
            // alone is not a success signal. After a failure the server-side watch
            // count can't have changed for this user, so there is nothing to sync
            onSuccess: (data) => {
              if (data?.code === 0 && orchestratorData?.instanceId) {
                syncSubstepStatus({ ...orchestratorData, handlerFields: { video_id: video.id } });
              }
            },
            // useCustomMutation doesn't forward an onError config and disableAlert
            // mutes its internal one — keep the retry alert reachable if the mutation
            // ever rejects (transport-level)
            onError: notifyCompletionFailed
          }
        );
        return;
      }

      if (!PROGRESS_EVENTS.includes(type)) return;

      const position = Number(currentTime) || 0;
      if (position <= 0) return;

      const lastSent = lastSentPositionRef.current;
      if (lastSent !== null && Math.abs(position - lastSent) < MIN_POSITION_DELTA_SECONDS) return;

      lastSentPositionRef.current = position;
      sendProgressUpdate({ video_id: video.id, current_position_seconds: position });
    },
    [
      isAdmin,
      video?.id,
      sendProgressUpdate,
      sendProgressComplete,
      syncSubstepStatus,
      orchestratorData
    ]
  );

  // Resume from the last saved position (only for an unfinished watch); wait out any
  // in-flight refetch so we never seek to a stale cached position
  useEffect(() => {
    if (!playerReady || progressFetching || hasResumedRef.current) return;

    const resumePosition = Number(savedProgress?.current_position_seconds) || 0;
    if (savedProgress?.is_completed || resumePosition <= 0) return;

    hasResumedRef.current = true;
    try {
      const player = playerRef.current?.getInternalPlayer?.();
      if (typeof player?.seekTo === 'function') {
        player.seekTo(resumePosition, 'seconds');
      } else if (player && 'currentTime' in player) {
        player.currentTime = resumePosition;
      }
    } catch (e) {
      // A failed resume is cosmetic (playback just starts at 0) — never block playback
      Honeybadger.notify(e, { context: { source: 'TrainingVideo resume' } });
    }
  }, [playerReady, progressFetching, savedProgress]);

  return { playerRef, hasEnded, trackProgress };
}
